import React, { useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { Download, Image } from "@mui/icons-material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useTranslation } from "react-i18next";
import { convertDateToHindiWords } from "../../utils/dateToWordsHindi";
import {
  formatBackendGradeForUi,
  formatTotalGradeForDisplay,
} from "../../utils/gradeDisplay";
import { AssessmentType } from "../../utils/gradeDisplay";
import type { GradeCategory } from "../../hooks/useMarkSheets";
import { useSchools } from "../../hooks/useSchools";
import { useSubjectMarksMappingByClassName } from "../../hooks/useSubjects";

/**
 * Static UI (HTML -> TSX conversion) for "समग्र प्रगति पत्रक" - Class 4.
 * Backend integration baad me hoga; abhi exact layout preserve karna goal hai.
 */
type SubjectLike = {
  subjectCode?: string;
  subjectName?: string;
  /** Monthly test (weightage 10) — API field */
  quarterlyWeightage?: number | null;
  /** Half-yearly (weightage 20) — API field */
  halfYearlyWeightage?: number | null;
  quarterlyTotal?: number | null;
  halfYearlyTotal?: number | null;
  theoryMarks?: number | null;
  practicalMarks?: number | null;
  grade?: string;
};

type StudentLike = {
  rollNumber?: string;
  enrolmentNo?: string;
  studentName?: string;
  firstName?: string;
  lastName?: string;
  fatherName?: string;
  motherName?: string;
  dateOfBirth?: string | number[];
  section?: string;
  className?: string;
  samagraId?: string;
  aadharNo?: string;
};

export interface ClassFourthMarksheetTemplateProps {
  student: StudentLike;
  schoolName: string;
  schoolCode: string;
  diseCode: string;
  block?: string;
  district?: string;
  marksheetType: string;
  sessionYear: string;
  className: string;
  subjects: SubjectLike[];
  percentage: number;
  totalGrade: string;
  result: string;
  rank: number;
  failSubjects: string;
  isPdf?: boolean;
  hideDownloadButton?: boolean;
  /** Used to load school logo for watermark/background. */
  school?: { id?: number; nameofSchool?: string } | null;
  marksheet_assessment?: Partial<Record<AssessmentType, GradeCategory>>;
}

const toDisplayName = (student: StudentLike) => {
  const n = (student.studentName || "").trim();
  if (n) return n;
  return `${student.firstName || ""} ${student.lastName || ""}`.trim() || "N/A";
};

const formatDob = (dob: StudentLike["dateOfBirth"]) => {
  if (Array.isArray(dob)) {
    const [y, m, d] = dob;
    if (y && m && d) return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
    return dob.filter(Boolean).join("/");
  }
  return dob ? String(dob) : "";
};

/** Display numeric marks / weightage (handles decimals from API). */
const vNum = (value: unknown) => {
  if (value === null || value === undefined || value === -1) return "";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  const rounded = Math.round(n * 100) / 100;
  if (Number.isInteger(rounded)) return String(rounded);
  return String(rounded);
};

const monthlyWeightage = (s: SubjectLike) =>
  s.quarterlyWeightage ?? s.quarterlyTotal;
const halfYearlyWeightage = (s: SubjectLike) =>
  s.halfYearlyWeightage ?? s.halfYearlyTotal;

const EMPTY_SUBJECT_ROW: SubjectLike = {};

const subjectNameCell = (s: SubjectLike) =>
  (s.subjectName || s.subjectCode || "").trim();

type Class4OrderedSubjects = {
  firstLang: SubjectLike;
  secondLang: SubjectLike;
  math: SubjectLike;
  evs: SubjectLike;
  extraLang: SubjectLike;
};

const getBaseClassName = (className?: string): string => {
  if (!className) return "";
  const match = className.match(/^(\d+(?:th|st|nd|rd)?)/i);
  return match ? match[1] : className.split(/\s+/)[0];
};

const normalizeSubjectCodeForMapping = (code: unknown): string => {
  const s = String(code ?? "").trim();
  if (!s) return "";
  if (/^\d+$/.test(s)) return s.padStart(3, "0");
  return s.toUpperCase();
};

const orderByMappingPriority = (
  subjects: SubjectLike[] | undefined,
  mapping: any[] | undefined
): SubjectLike[] => {
  const list = [...(subjects || [])].filter(
    (s) =>
      s &&
      (String(s.subjectName || "").trim() || String(s.subjectCode || "").trim())
  );
  const pri = new Map<string, number>();
  (mapping || []).forEach((m: any) => {
    const key = normalizeSubjectCodeForMapping(m?.subjectCode);
    const p = m?.priorityNo ?? m?.priority_no ?? m?.priority ?? m?.priorityNumber;
    const n = Number(p);
    if (key && Number.isFinite(n)) pri.set(key, n);
  });
  if (pri.size === 0) return list;

  return list
    .map((s, idx) => ({ s, idx }))
    .sort((a, b) => {
      const ak = normalizeSubjectCodeForMapping(a.s.subjectCode);
      const bk = normalizeSubjectCodeForMapping(b.s.subjectCode);
      const ap = ak ? pri.get(ak) : undefined;
      const bp = bk ? pri.get(bk) : undefined;
      const ah = typeof ap === "number";
      const bh = typeof bp === "number";
      if (ah && bh) return ap! - bp!;
      if (ah) return -1;
      if (bh) return 1;
      return a.idx - b.idx;
    })
    .map((x) => x.s);
};

/**
 * Prefer matching by subjectName/subjectCode (more reliable than array index ordering).
 * Falls back to remaining subjects in original order.
 */
function orderSubjectsForClassFourthMarksheet(
  subjects: SubjectLike[] | undefined
): Class4OrderedSubjects {
  const list = [...(subjects || [])].filter(
    (s) =>
      s &&
      (String(s.subjectName || "").trim() || String(s.subjectCode || "").trim())
  );
  const pool = [...list];

  const blobFor = (s: SubjectLike) =>
    `${String(s.subjectName || "").toLowerCase()} ${String(s.subjectCode || "").toLowerCase()}`;

  const takeFirstMatch = (aliases: readonly string[]) => {
    const idx = pool.findIndex((s) => {
      const blob = blobFor(s);
      return aliases.some((a) => blob.includes(a.toLowerCase()));
    });
    if (idx >= 0) {
      const s = pool[idx]!;
      pool.splice(idx, 1);
      return s;
    }
    return null;
  };

  const firstLang =
    takeFirstMatch(["hindi", "हिंदी"]) ||
    takeFirstMatch(["english", "इंग्लिश", "अंग्रेजी", "अंग्रेज़ी"]) ||
    pool.shift() ||
    EMPTY_SUBJECT_ROW;

  const secondLang =
    takeFirstMatch(["english", "इंग्लिश", "अंग्रेजी", "अंग्रेज़ी"]) ||
    takeFirstMatch(["urdu", "उर्दू"]) ||
    takeFirstMatch(["sanskrit", "संस्कृत"]) ||
    pool.shift() ||
    EMPTY_SUBJECT_ROW;

  const math =
    takeFirstMatch(["math", "mathematics", "गणित"]) ||
    pool.shift() ||
    EMPTY_SUBJECT_ROW;

  const evs =
    takeFirstMatch(["evs", "environment", "पर्यावरण", "पर्यावरण अध्ययन"]) ||
    pool.shift() ||
    EMPTY_SUBJECT_ROW;

  const extraLang =
    takeFirstMatch(["additional", "extra", "अतिरिक्त"]) ||
    pool.shift() ||
    EMPTY_SUBJECT_ROW;

  return { firstLang, secondLang, math, evs, extraLang };
}

const subjectLabelWithName = (fixedLabel: string, s: SubjectLike) => {
  const name = (s.subjectName || "").trim();
  return (
    <>
      {fixedLabel}{" "}
      {name ? <span className="">({name})</span> : null}
    </>
  );
};

const ClassFourthMarksheetTemplate: React.FC<ClassFourthMarksheetTemplateProps> = ({
  student,
  schoolName,
  schoolCode,
  diseCode,
  block,
  district,
  marksheetType,
  sessionYear,
  className,
  subjects,
  percentage,
  totalGrade,
  result,
  isPdf = false,
  hideDownloadButton = false,
  school,
  marksheet_assessment,
}) => {
  const { t } = useTranslation();
  const { useSchoolLogoInfo } = useSchools(false, { disableSchoolAdmins: true });
  const baseClass = getBaseClassName(className || student.className || "");
  const { data: subjectMarksMapping } = useSubjectMarksMappingByClassName(baseClass || undefined);
  const schoolIdForLogo = school?.id;
  const { data: logoInfo, isLoading: logoLoading } = useSchoolLogoInfo(schoolIdForLogo);
  const templateRef = useRef<HTMLDivElement>(null);
  const [isDownloadingImage, setIsDownloadingImage] = useState(false);
  const [isDownloadingPdfFromImage, setIsDownloadingPdfFromImage] =
    useState(false);

  const dobNumeric = useMemo(
    () => formatDob(student.dateOfBirth),
    [student.dateOfBirth]
  );
  const dobInWordsHindi = useMemo(
    () => convertDateToHindiWords(dobNumeric),
    [dobNumeric]
  );

  const { firstLang, secondLang, math, evs, extraLang } = useMemo(() => {
    const ordered = orderByMappingPriority(subjects, subjectMarksMapping as any);
    // Preserve existing table structure: fill slots in priority order.
    return orderSubjectsForClassFourthMarksheet(ordered);
  }, [subjects, subjectMarksMapping]);

  const assessmentGrade = (k: AssessmentType) =>
    formatBackendGradeForUi(marksheet_assessment?.[k] as any);

  const fileBase = useMemo(() => {
    const studentPart = (toDisplayName(student) || "student")
      .toString()
      .trim()
      .replace(/\s+/g, "-");
    const cls = (className || student.className || "class")
      .toString()
      .trim()
      .replace(/\s+/g, "-");
    const sess = (sessionYear || "session")
      .toString()
      .trim()
      .replace(/\s+/g, "-");
    const type = (marksheetType || "marksheet")
      .toString()
      .trim()
      .replace(/\s+/g, "-");
    const date = new Date().toISOString().split("T")[0];
    return `marksheet-${studentPart}-${cls}-${sess}-${type}-${date}`;
  }, [student, className, sessionYear, marksheetType]);

  const downloadAsImage = async () => {
    if (!templateRef.current) return;

    setIsDownloadingImage(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));

      const element = templateRef.current;
      const canvas = await html2canvas(element, {
        useCORS: true,
        allowTaint: false,
        background: "#ffffff",
        scale: 3.0,
        // Use offset* here (same as PDF) to prevent right-side cropping
        width: element.offsetWidth,
        height: element.offsetHeight,
        windowWidth: element.offsetWidth,
        windowHeight: element.offsetHeight,
        scrollX: 0,
        scrollY: 0,
        logging: false,
        removeContainer: false,
        imageTimeout: 15000,
        onclone: (clonedDoc: Document) => {
          const clonedElement = clonedDoc.body.firstElementChild as HTMLElement;
          if (clonedElement) {
            clonedElement.style.backgroundColor = "#ffffff";
            clonedElement.style.overflow = "visible";
          }
        },
      } as any);

      const link = document.createElement("a");
      link.download = `${fileBase}.png`;
      link.href = canvas.toDataURL("image/png", 0.92);
      link.click();
    } catch (error) {
      alert(t("failed_to_download_image"));
    } finally {
      setIsDownloadingImage(false);
    }
  };

  const downloadAsPdfFromImage = async () => {
    if (!templateRef.current) return;

    setIsDownloadingPdfFromImage(true);
    try {
      const element = templateRef.current;
      await new Promise((resolve) => setTimeout(resolve, 300));

      const canvas = await html2canvas(element, {
        useCORS: true,
        allowTaint: false,
        background: "#ffffff",
        scale: 3.0,
        width: element.offsetWidth,
        height: element.offsetHeight,
        windowWidth: element.offsetWidth,
        windowHeight: element.offsetHeight,
        logging: false,
        removeContainer: false,
        imageTimeout: 15000,
        onclone: (clonedDoc: Document) => {
          const clonedElement = clonedDoc.body.firstElementChild as HTMLElement;
          if (clonedElement) clonedElement.style.backgroundColor = "#ffffff";
        },
      } as any);

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "portrait",
        compress: true,
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      /**
       * Always scale to full page width (no left/right letterboxing). If the marksheet is taller
       * than one A4 page at that width, continue on extra pages (negative Y offset pattern).
       */
      const imgWidthMm = pageWidth;
      const imgHeightMm = (canvas.height * imgWidthMm) / canvas.width;

      let heightPrintedMm = 0;
      let pageIndex = 0;
      while (heightPrintedMm < imgHeightMm) {
        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(
          imgData,
          "PNG",
          0,
          -heightPrintedMm,
          imgWidthMm,
          imgHeightMm
        );
        heightPrintedMm += pageHeight;
        pageIndex += 1;
      }
      pdf.save(`${fileBase}.pdf`);
    } catch (error) {
      alert(
        t("failed_to_download_pdf").replace(
          "{error}",
          error instanceof Error ? error.message : "Unknown error"
        )
      );
    } finally {
      setIsDownloadingPdfFromImage(false);
    }
  };

  return (
    <div className="progress-marksheet-static-class4">
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .progress-marksheet-static-class4 * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        .progress-marksheet-static-class4 {
          font-family: Arial, sans-serif;
          background-color: #f2f2f2;
          display: flex;
          justify-content: center;
          padding: 20px;
          width: 100%;
          min-height: 100vh;
        }

        .progress-marksheet-static-class4 .sheet-and-actions {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
          gap: 22px;
          padding-bottom: 10px;
        }

        .progress-marksheet-static-class4 .marksheet-wrapper {
          background-color: #fff;
          width: 210mm; /* A4 width */
          min-width: 210mm;
          min-height: 287mm;
          padding: 6px;
          border: 8px double #8B0000;
          box-shadow: 0 0 10px rgba(0,0,0,0.3);
          position: relative;
          overflow: hidden;
        }

        .inner-border {
          padding: 0px 5px;
          display: flex;
          flex-direction: column;
          position: relative;
          z-index: 1;
          background: transparent;
        }

        /* Header Sections */
        .header {
          display: flex;
          align-items: center;
          gap: 5px;
          margin-bottom: 5px;
        }

        .logo-left {
          width: 76px;
          height: 76px;
          flex-shrink: 0;
          border-radius: 50%;
          border: 1px solid #000;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #fff;
          box-sizing: border-box;
        }

        .logo-left img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .title-container {
          flex: 1;
          text-align: center;
          min-width: 0;
        }

        .title-container h1 {
          font-size: 26px;
          font-weight: bold;
          color: #000;
        }

        .title-container h2 {
          font-size: 22px;
          font-weight: bold;
          color: #000;
        }

        .title-container h3 {
          font-size: 18px;
          font-weight: bold;
          color: #000;
        }

        /* Personal Details */
        .personal-details {
          margin-bottom: 5px;
          padding: 8px;
          border: 1px solid #000;
        }

        .row {
          display: flex;
          align-items: flex-end;
          margin-bottom: 4px;
        }

        .label {
          font-size: 14px;
          white-space: nowrap;
          font-weight: bold;
        }

        .dots {
          border-bottom: 1px dotted #000;
          margin: 0 10px;
        }

        .flex-grow {
          flex-grow: 1;
        }

        .slash {
          font-weight: bold;
          margin: 0 10px;
          font-size: 16px;
        }

        /* Tables Styling */
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 10px;
          background: transparent;
        }

        .progress-marksheet-static-class4 th, .progress-marksheet-static-class4 td {
          border: 1px solid #000;
          padding: 5px 6px;
          font-size: 14px;
          background: transparent;
        }

        .progress-marksheet-static-class4 th {
          font-weight: bold;
          text-align: center;
          background-color: #f4e1d2; /* peach color according to image */
        }

        .progress-marksheet-static-class4 .overall-grading tbody tr:first-child td {
          background-color: #f4e1d2;
        }

        .text-center {
          text-align: center;
        }

        .text-left {
          text-align: left;
        }

        .font-bold {
          font-weight: bold;
        }

        /* Watermark layout for table 1 */
        .academic-evaluation {
          position: relative;
          overflow: hidden;
        }

        /* The Watermark Overlay */
        .watermark {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(-30deg);
          font-size: 48px;
          color: rgba(0, 0, 0, 0.08);
          font-weight: bold;
          text-align: center;
          white-space: nowrap;
          z-index: 0;
          pointer-events: none;
          line-height: 1.2;
        }

        /* Bring table content above watermark */
        .academic-evaluation table {
          position: relative;
          z-index: 1;
        }

        /* Academic Table Layout */
        .col-subject { width: 22%; }
        .col-total { width: 8%; }
        .col-grade { width: 14%; }
        .col-overall { width: 14%; }

        /* Co-scholastic Tables */
        .co-scholastic-personal {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          margin-top: 6px;
          margin-bottom: 6px;
        }

        .left-table, .right-table {
          flex: 1;
        }

        .left-table table, .right-table table {
          margin-bottom: 0;
        }

        .col-grade-small {
          width: 30%;
        }

        /* Note Section */
        .progress-marksheet-static-class4 .note {
          margin-bottom: 0px;
        }

        /* Promotion Details enclosed box */
        .progress-marksheet-static-class4 .promotion-details {
          display: flex;
          align-items: flex-end;
          margin-bottom: 0px;
          padding-top: 2px;
        }

        /* Footer Signatures */
        .signatures {
          display: flex;
          justify-content: space-between;
          margin-top: auto;
          padding-top: 18px;
        }

        .sig-left, .sig-right {
          text-align: center;
          font-size: 13px;
        }

        .sig-left p, .sig-right p {
        }
      `}</style>

      <div className="sheet-and-actions">
        <div ref={templateRef} className="marksheet-wrapper">
          {/* Background watermark logo + soft gradient bands (same as MarksheetTemplate) */}
          {logoInfo?.fileName ? (
            <>
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 0,
                  pointerEvents: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={`${process.env.REACT_APP_API_URL}/schools/logos/${logoInfo.fileName}`}
                  alt=""
                  style={{
                    width: "60%",
                    height: "auto",
                    opacity: 0.2,
                    objectFit: "contain",
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 0,
                  pointerEvents: "none",
                  background:
                    "linear-gradient(to right, rgba(144, 238, 144, 0.15) 0%, rgba(255, 255, 153, 0.15) 50%, rgba(255, 160, 160, 0.15) 100%)",
                }}
              />
            </>
          ) : (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 0,
                pointerEvents: "none",
                background:
                  "linear-gradient(to right, rgba(144, 238, 144, 0.15) 0%, rgba(255, 255, 153, 0.15) 50%, rgba(255, 160, 160, 0.15) 100%)",
              }}
            />
          )}
          <div className="inner-border">
          {/* HEADER */}
          <header className="header">
            <div className="logo-left">
              {logoLoading ? (
                <CircularProgress size={28} sx={{ color: "#333" }} />
              ) : logoInfo?.fileName ? (
                <img
                  src={`${process.env.REACT_APP_API_URL}/schools/logos/${logoInfo.fileName}`}
                  alt={schoolName ? `${schoolName} logo` : "School logo"}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              ) : null}
            </div>

            <div className="title-container">
              <h1>राज्य शिक्षा केन्द्र (म.प्र.)</h1>
              <h2>समग्र प्रगति पत्रक</h2>
              <h3>
                कक्षा-{className || student.className || "4"} : सत्र {sessionYear || "N/A"}
              </h3>
            </div>
          </header>

          {/* PERSONAL DETAILS */}
          <section className="personal-details">
            <div className="row">
              <span className="label">विद्यार्थी का नाम (हिन्दी में):</span>
              <span className="dots flex-grow">{toDisplayName(student)}</span>
              <span className="label">(अंग्रेजी में):</span>
              <span className="dots flex-grow">{toDisplayName(student)}</span>
            </div>
            <div className="row">
              <span className="label">पिता का नाम: श्री</span>
              <span className="dots flex-grow">{student.fatherName || ""}</span>
              <span className="label">माता का नाम: श्रीमती</span>
              <span className="dots flex-grow">{student.motherName || ""}</span>
            </div>
            <div className="row">
              <span className="label">जन्म तिथि (अंकों में)</span>
              <span className="dots flex-grow">{formatDob(student.dateOfBirth)}</span>
              <span className="label">(शब्दों में)</span>
              <span className="dots flex-grow">{dobInWordsHindi}</span>
            </div>
            <div className="row">
              <span className="label">स्कॉलर / पंजी क्रमांक</span>
              <span className="dots flex-grow">{student.enrolmentNo || ""}</span>
              <span className="label">अनुक्रमांक:</span>
              <span className="dots flex-grow">{student.rollNumber || ""}</span>
              <span className="label">विकासखण्ड:</span>
              <span className="dots flex-grow">{block || ""}</span>
            </div>
            <div className="row">
              <span className="label">समग्र आईडी:</span>
              <span className="dots flex-grow">{student.samagraId || ""}</span>
              <span className="label">आधार आईडी:</span>
              <span className="dots flex-grow">{student.aadharNo || ""}</span>
              <span className="label">जिला:</span>
              <span className="dots flex-grow">{district || ""}</span>
            </div>
            <div className="row">
              <span className="label">
                विशेष आवश्यकता वाले बच्चे (CWSN) यदि हो तो टिक (✓) करें: HI / LV / VI / OH / MR / LD / MD /
                CP / Other
              </span>
              <span className="dots flex-grow"></span>
            </div>
            <div className="row">
              <span className="label">शाला का नाम व डाइसकोड:</span>
              <span className="dots flex-grow">{schoolName || ""}</span>
              <span className="slash">/</span>
              <span className="dots flex-grow">{diseCode || ""}</span>
            </div>
            {/* <div className="row">
              <span className="label">विकासखण्ड:</span>
              <span className="dots flex-grow">{block || ""}</span>
              <span className="label">जिला:</span>
              <span className="dots flex-grow">{district || ""}</span>
            </div> */}
          </section>

          {/* ACADEMIC EVALUATION TABLE */}
          <section className="academic-evaluation">
            {/* <div className="watermark">
              म. प्र. राज्य शिक्षा केन्द्र
              <br />
              अनुसंधान और प्रशिक्षण परिषद्
            </div> */}

            <table style={{ borderCollapse: "collapse" }}>
              <thead style={{ borderCollapse: "collapse" }}>
                <tr>
                  <th rowSpan={2} className="col-subject">
                    विषय
                  </th>
                  <th rowSpan={2} className="col-total">
                    पूर्णांक
                  </th>
                  <th colSpan={5}>शैक्षिक मूल्यांकन (ग्रेड)</th>
                </tr>
                <tr>
                  <th className="col-grade">
                    मासिक टेस्ट
                    <br />
                    <p style={{ fontSize: "10px" }}>(अधिभार 10 अंक)</p>
                  </th>
                  <th className="col-grade">
                    अर्द्धवार्षिक परीक्षा
                    <br />
                    <p style={{ fontSize: "10px" }}>(अधिभार 20 अंक)</p>
                  </th>
                  <th className="col-grade">
                    वार्षिक परीक्षा
                    <br />
                    लिखित
                    <br />
                    <p style={{ fontSize: "10px" }}>(अ.अ. 60)</p>
                  </th>
                  <th className="col-grade">
                    वार्षिक परीक्षा
                    <br />
                    प्रोजेक्ट
                    <br />
                    <p style={{ fontSize: "10px" }}>(अधिभार 10 अंक)</p>
                  </th>
                  <th className="col-overall">
                    समेकित
                    <br />
                    ग्रेड
                  </th>
                </tr>
              </thead>

              <tbody style={{ borderCollapse: "collapse" }}>
                <tr>
                  <td className="text-left font-bold">
                    {subjectNameCell(firstLang)}
                  </td>
                  <td className="text-center font-bold">100</td>
                  <td className="text-center">{vNum(monthlyWeightage(firstLang))}</td>
                  <td className="text-center">{vNum(halfYearlyWeightage(firstLang))}</td>
                  <td className="text-center">{vNum(firstLang.theoryMarks)}</td>
                  <td className="text-center">{vNum(firstLang.practicalMarks)}</td>
                  <td className="text-center">{formatBackendGradeForUi(firstLang.grade)}</td>
                </tr>
                <tr>
                  <td className="text-left font-bold">
                    {subjectNameCell(secondLang)}
                  </td>
                  <td className="text-center font-bold">100</td>
                  <td className="text-center">{vNum(monthlyWeightage(secondLang))}</td>
                  <td className="text-center">{vNum(halfYearlyWeightage(secondLang))}</td>
                  <td className="text-center">{vNum(secondLang.theoryMarks)}</td>
                  <td className="text-center">{vNum(secondLang.practicalMarks)}</td>
                  <td className="text-center">{formatBackendGradeForUi(secondLang.grade)}</td>
                </tr>
                <tr>
                  <td className="text-left font-bold">
                    {subjectNameCell(math)}
                  </td>
                  <td className="text-center font-bold">100</td>
                  <td className="text-center">{vNum(monthlyWeightage(math))}</td>
                  <td className="text-center">{vNum(halfYearlyWeightage(math))}</td>
                  <td className="text-center">{vNum(math.theoryMarks)}</td>
                  <td className="text-center">{vNum(math.practicalMarks)}</td>
                  <td className="text-center">{formatBackendGradeForUi(math.grade)}</td>
                </tr>
                <tr>
                  <td className="text-left font-bold">{subjectNameCell(evs)}</td>
                  <td className="text-center font-bold">100</td>
                  <td className="text-center">{vNum(monthlyWeightage(evs))}</td>
                  <td className="text-center">{vNum(halfYearlyWeightage(evs))}</td>
                  <td className="text-center">{vNum(evs.theoryMarks)}</td>
                  <td className="text-center">{vNum(evs.practicalMarks)}</td>
                  <td className="text-center">{formatBackendGradeForUi(evs.grade)}</td>
                </tr>
                <tr>
                  <td className="text-center font-bold">ग्रेड</td>
                  <td className="text-center"></td>
                  <td className="text-center"></td>
                  <td className="text-center"></td>
                  <td className="text-center"></td>
                  <td className="text-center"></td>
                  <td className="text-center">{formatTotalGradeForDisplay(totalGrade)}</td>
                </tr>
                <tr>
                  <td className="text-left font-bold">
                    {subjectNameCell(extraLang)}
                  </td>
                  <td className="text-center font-bold">100</td>
                  <td className="text-center">{vNum(monthlyWeightage(extraLang))}</td>
                  <td className="text-center">{vNum(halfYearlyWeightage(extraLang))}</td>
                  <td className="text-center">{vNum(extraLang.theoryMarks)}</td>
                  <td className="text-center">{vNum(extraLang.practicalMarks)}</td>
                  <td className="text-center">{formatBackendGradeForUi(extraLang.grade)}</td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* CO-SCHOLASTIC & PERSONAL QUALITY TABLES */}
          <section className="co-scholastic-personal">
            <div className="left-table">
              <table style={{ borderCollapse: "collapse" }}>
                <thead style={{ borderCollapse: "collapse" }}>
                  <tr>
                    <th style={{ fontSize: "11.5px" }} colSpan={2}>
                      सह-शैक्षिक क्षेत्र
                    </th>
                  </tr>
                  <tr>
                    <th style={{ fontSize: "11.5px" }}>क्षेत्र</th>
                    <th
                      style={{ fontSize: "11.5px" }}
                      className="col-grade-small"
                    >
                      ग्रेड
                    </th>
                  </tr>
                </thead>
                <tbody style={{ borderCollapse: "collapse" }}>
                  <tr>
                    <td style={{ fontSize: "11.5px" }} className="font-bold">
                      साहित्यिक
                    </td>
                    <td className="text-center">{assessmentGrade(AssessmentType.SAHITYIK)}</td>
                  </tr>
                  <tr>
                    <td style={{ fontSize: "11.5px" }} className="font-bold">
                      सांस्कृतिक
                    </td>
                    <td className="text-center">{assessmentGrade(AssessmentType.SANSKRITIK)}</td>
                  </tr>
                  <tr>
                    <td style={{ fontSize: "11.5px" }} className="font-bold">
                      वैज्ञानिक
                    </td>
                    <td className="text-center">{assessmentGrade(AssessmentType.VAIGYANIK)}</td>
                  </tr>
                  <tr>
                    <td style={{ fontSize: "11.5px" }} className="font-bold">
                      सृजनात्मक
                    </td>
                    <td className="text-center">{assessmentGrade(AssessmentType.SRIJANATMAK)}</td>
                  </tr>
                  <tr>
                    <td style={{ fontSize: "11.5px" }} className="font-bold">
                      खेलकूद
                    </td>
                    <td className="text-center">{assessmentGrade(AssessmentType.KHELKUD)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="right-table">
              <table style={{ borderCollapse: "collapse" }}>
                <thead style={{ borderCollapse: "collapse" }}>
                  <tr>
                    <th style={{ fontSize: "11.5px" }} colSpan={2}>
                      व्यक्तिगत-सामाजिक गुण
                    </th>
                  </tr>
                  <tr>
                    <th style={{ fontSize: "11.5px" }}>क्षेत्र</th>
                    <th
                      style={{ fontSize: "11.5px" }}
                      className="col-grade-small"
                    >
                      ग्रेड
                    </th>
                  </tr>
                </thead>
                <tbody style={{ borderCollapse: "collapse" }}>
                  <tr>
                    <td style={{ fontSize: "11.5px" }} className="font-bold">
                      नियमितता एवं समयबद्वता
                    </td>
                    <td className="text-center">{assessmentGrade(AssessmentType.REGULARITY)}</td>
                  </tr>
                  <tr>
                    <td style={{ fontSize: "11.5px" }} className="font-bold">
                      स्वच्छता
                    </td>
                    <td className="text-center">{assessmentGrade(AssessmentType.CLEANLINESS)}</td>
                  </tr>
                  <tr>
                    <td style={{ fontSize: "11.5px" }} className="font-bold">
                      अनुशासन / कर्तव्यनिष्ठा
                    </td>
                    <td className="text-center">{assessmentGrade(AssessmentType.DISCIPLINE)}</td>
                  </tr>
                  <tr>
                    <td style={{ fontSize: "11.5px" }} className="font-bold">
                      सहयोग की भावना / अभिवृत्ति
                    </td>
                    <td className="text-center">{assessmentGrade(AssessmentType.COOPERATION)}</td>
                  </tr>
                  <tr>
                    <td style={{ fontSize: "11.5px" }} className="font-bold">
                      पर्यावरण के प्रति संवेदनशीलता
                    </td>
                    <td className="text-center">{assessmentGrade(AssessmentType.ENVIRONMENT)}</td>
                  </tr>
                  <tr>
                    <td style={{ fontSize: "11.5px" }} className="font-bold">
                      नेतृत्व की क्षमता
                    </td>
                    <td className="text-center">{assessmentGrade(AssessmentType.LEADERSHIP)}</td>
                  </tr>
                  <tr>
                    <td style={{ fontSize: "11.5px" }} className="font-bold">
                      सत्यवादिता एवं ईमानदारी
                    </td>
                    <td className="text-center">{assessmentGrade(AssessmentType.HONESTY)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* OVERALL GRADING CRITERIA */}
          <section className="overall-grading">
            <table style={{ borderCollapse: "collapse" }}>
              <thead style={{ borderCollapse: "collapse" }}>
                <tr>
                  <th style={{ fontSize: "10px", padding: "2px", fontWeight:"normal" }} colSpan={9}>
                    शैक्षिक मूल्यांकन हेतु समेकित ग्रेड का विवरण
                  </th>
                </tr>
              </thead>
              <tbody style={{ borderCollapse: "collapse" }}>
                <tr>
                  <th
                    style={{ fontSize: "9px", padding: "2px" }}
                    className="font-bold text-center"
                  >
                    ग्रेड
                  </th>
                  <th
                    style={{ fontSize: "9px", padding: "2px" }}
                    className="text-center font-bold"
                  >
                    A+
                  </th>
                  <th
                    style={{ fontSize: "9px", padding: "2px" }}
                    className="text-center font-bold"
                  >
                    A
                  </th>
                  <th
                    style={{ fontSize: "9px", padding: "2px" }}
                    className="text-center font-bold"
                  >
                    B+
                  </th>
                  <th
                    style={{ fontSize: "9px", padding: "2px" }}
                    className="text-center font-bold"
                  >
                    B
                  </th>
                  <th
                    style={{ fontSize: "9px", padding: "2px" }}
                    className="text-center font-bold"
                  >
                    C+
                  </th>
                  <th
                    style={{ fontSize: "9px", padding: "2px" }}
                    className="text-center font-bold"
                  >
                    C
                  </th>
                  <th
                    style={{ fontSize: "9px", padding: "2px" }}
                    className="text-center font-bold"
                  >
                    D
                  </th>
                  <th
                    style={{ fontSize: "9px", padding: "2px" }}
                    className="text-center font-bold"
                  >
                    E
                  </th>
                </tr>
                <tr>
                  <td
                    style={{ fontSize: "9px", padding: "2px" }}
                    className="font-bold text-center"
                  >
                    प्राप्तांक (प्रतिशत में)
                  </td>
                  <td style={{ fontSize: "9px", padding: "2px" }} className="text-center">
                    85 से अधिक
                  </td>
                  <td style={{ fontSize: "9px", padding: "2px" }} className="text-center">
                    76-85
                  </td>
                  <td style={{ fontSize: "9px", padding: "2px" }} className="text-center">
                    66-75
                  </td>
                  <td style={{ fontSize: "9px", padding: "2px" }} className="text-center">
                    56-65
                  </td>
                  <td style={{ fontSize: "9px", padding: "2px" }} className="text-center">
                    51-55
                  </td>
                  <td style={{ fontSize: "9px", padding: "2px" }} className="text-center">
                    46-50
                  </td>
                  <td style={{ fontSize: "9px", padding: "2px" }} className="text-center">
                    33-45
                  </td>
                  <td style={{ fontSize: "9px", padding: "2px" }} className="text-center">
                    33 से कम (सुधार योग्य)
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* NOTE */}
          <div className="note">
            <span style={{ fontSize: "10px" }}>
              नोट - वार्षिक परीक्षा फल निर्धारण में सह-शैक्षिक क्षेत्र एवं व्यक्तिगत-सामाजिक गुण में अंकित ग्रेड को
              नहीं जोड़ा गया है।
            </span>
          </div>

          {/* PROMOTION DETAILS */}
          <div className="promotion-details" style={{ fontSize: "13px" }}>
            <span className="label" style={{ fontSize: "14px" , fontWeight:"normal"}}>
              विद्यार्थी का परीक्षाफल एवं ग्रेड -{" "}
            </span>
            <span className="dots flex-grow">{`${result || ""}${totalGrade ? ` (${formatTotalGradeForDisplay(totalGrade)})` : ""}`}</span>
            <span className="label" style={{ fontSize: "14px" , fontWeight:"normal"}}>
              विद्यार्थी को कक्षा{" "}
            </span>
            <span className="dots flex-grow">{className || student.className || ""}</span>
            <span className="label" style={{ fontSize: "14px" , fontWeight:"normal"}}>
              {" "}
              में कक्षोन्नत किया जाता है।
            </span>
          </div>

          {/* SIGNATURES */}
          <footer className="signatures">
            <div className="sig-left">
              <p>हस्ताक्षर</p>
              <p>कक्षा शिक्षक</p>
            </div>
            <div className="sig-right">
              <p>हस्ताक्षर</p>
              <p>संस्था प्रमुख (पदमुद्रा सहित)</p>
            </div>
          </footer>
          </div>
        </div>

        {/* Download Buttons - Only show if not PDF and not hidden */}
        {!isPdf && !hideDownloadButton && (
          <Box sx={{ marginTop: "16px", textAlign: "center" }}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                startIcon={<Image />}
                disabled={isDownloadingImage}
                onClick={downloadAsImage}
                sx={{
                  padding: "16px 34px !important",
                  fontSize: 16,
                  fontWeight: 600,
                  minWidth: "240px",
                  minHeight: "52px",
                  position: "relative",
                  backgroundColor: "#9c27b0",
                  borderRadius: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  boxShadow: "0 4px 12px rgba(156, 39, 176, 0.3)",
                  "&:hover": {
                    backgroundColor: "#7b1fa2",
                    boxShadow: "0 6px 16px rgba(156, 39, 176, 0.4)",
                    transform: "translateY(-2px)",
                  },
                  "&:disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                  transition: "all 0.3s ease-in-out",
                }}
              >
                {isDownloadingImage ? (
                  <>
                    <Box
                      sx={{
                        display: "inline-block",
                        width: "16px",
                        height: "16px",
                        border: "2px solid #fff",
                        borderTop: "2px solid transparent",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                        marginRight: "8px",
                      }}
                    />
                    {t("generating")} {t("image")}...
                  </>
                ) : (
                  "Download as Image"
                )}
              </Button>

              <Button
                variant="contained"
                startIcon={<Download />}
                disabled={isDownloadingPdfFromImage}
                onClick={downloadAsPdfFromImage}
                sx={{
                  padding: "16px 34px !important",
                  fontSize: 16,
                  fontWeight: 600,
                  minWidth: "240px",
                  minHeight: "52px",
                  position: "relative",
                  backgroundColor: "#2196f3",
                  borderRadius: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  boxShadow: "0 4px 12px rgba(33, 150, 243, 0.3)",
                  "&:hover": {
                    backgroundColor: "#1976d2",
                    boxShadow: "0 6px 16px rgba(33, 150, 243, 0.4)",
                    transform: "translateY(-2px)",
                  },
                  "&:disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                  transition: "all 0.3s ease-in-out",
                }}
              >
                {isDownloadingPdfFromImage ? (
                  <>
                    <Box
                      sx={{
                        display: "inline-block",
                        width: "16px",
                        height: "16px",
                        border: "2px solid #fff",
                        borderTop: "2px solid transparent",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                        marginRight: "8px",
                      }}
                    />
                    {t("generating")} PDF...
                  </>
                ) : (
                  "Download as PDF"
                )}
              </Button>
            </Box>

            {(isDownloadingImage || isDownloadingPdfFromImage) && (
              <Box sx={{ marginTop: "12px" }}>
                <Typography variant="body2" color="text.secondary">
                  {isDownloadingImage
                    ? t("please_wait_marksheet_image")
                    : t("please_wait_marksheet_pdf")}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </div>
    </div>
  );
};

export default ClassFourthMarksheetTemplate;

